import React, { useState, useEffect } from 'react';
import { ArrowLeft, CheckCircle, XCircle, FileText, MapPin, Building2, Eye } from 'lucide-react';

import { useNavigate, useParams } from 'react-router-dom';
import { FILE_BASE_URL, API_BASE_URL } from '../api/config';
import API from '../api';

import { showSuccess, showError, redirectPage } from '../utils/notification';
import { useAuth } from '../auth/AuthContext';

// Activity categories used to expand the comma-encoded `activity_ids` stored
// on application_hdr (e.g. "1:Strengthening of bund,4:Fencing").
const ACTIVITY_CATEGORIES = {
  1: 'Desilting/ Deepening',
  2: 'Repair/ rehabilitation of hydraulic structure',
  3: 'Removal of obstruction in water bodies',
  4: 'Infrastructure Arrangements',
  5: 'Quality checking of water',
  6: 'Five years AMC by any organization',
};

const formatDate = (v) => (v ? new Date(v).toLocaleDateString('en-IN') : '-');

// FRS: SLA is 10 working days from the date the application was received at
// the current level. For L1 the clock starts at submission; for L2 it starts
// when L1 forwards. We approximate by using each chain row's action_date
// (when the row last changed) or, for L1 only, the application created_at.
const SLA_DAYS = 10;
const addWorkingDays = (start, days) => {
  const d = new Date(start);
  let added = 0;
  while (added < days) {
    d.setDate(d.getDate() + 1);
    const dow = d.getDay();
    if (dow !== 0 && dow !== 6) added += 1;
  }
  return d;
};
const slaForStep = (app, step) => {
  if (!app || !step) return null;
  const start = step.level === 1 ? app.created_at : step.action_date || app.created_at;
  if (!start) return null;
  const due = addWorkingDays(start, SLA_DAYS);
  const now = new Date();
  const remainingMs = due - now;
  const daysLeft = Math.ceil(remainingMs / (1000 * 60 * 60 * 24));
  return { due, daysLeft, overdue: remainingMs < 0 };
};

const stripNocPrefix = (raw) => {
  if (!raw) return '';
  const m = String(raw).match(/(\d+)$/);
  return m ? m[1] : '';
};

export default function WorkflowPage() {
  const { id: rawId } = useParams();
  const navigate = useNavigate();

  const applicationId = stripNocPrefix(rawId);

  const [loading, setLoading] = useState(true);
  const [application, setApplication] = useState(null);
  const [tank, setTank] = useState(null);
  const [ngo, setNgo] = useState(null);
  const [department, setDepartment] = useState(null);
  const [district, setDistrict] = useState(null);
  const [chain, setChain] = useState([]);
  const [chainUsers, setChainUsers] = useState({});
  const [docs, setDocs] = useState([]);
  const [showNGOProfile, setShowNGOProfile] = useState(false);
  const [actionMode, setActionMode] = useState(null); // 'approve' | 'reject' | 'return' | null
  const [comments, setComments] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState('');

  // FRS: mandatory reasons listed in a dropdown for Reject / Return.
  // Department-provided list will replace this once finalised.
  const REJECT_REASONS = [
    'Documents incomplete or invalid',
    'Application details do not match site records',
    'Waterbody not eligible under current scheme',
    'Duplicate application',
    'Other',
  ];
  const RETURN_REASONS = [
    'Clarification required on activity scope',
    'Additional supporting documents needed',
    'Revised cost estimate required',
    'Field-visit feedback to be incorporated',
    'Other',
  ];

  // Auth from context (pen-test remediation — no localStorage).
  const { user: userInfo, role: userRole, isReady: authReady, isAuthenticated } = useAuth();
  const userId = userInfo?.user_id;

  useEffect(() => {
    if (authReady && !isAuthenticated) {
      navigate('/login');
      return;
    }
    if (!authReady || !isAuthenticated) return;
    if (!applicationId) {
      showError('Invalid application id');
      return;
    }

    const safeGet = (url) =>
      API.get(url)
        .then((r) => r?.data?.data ?? null)
        .catch((err) => {
          console.error(`GET ${url} failed:`, err?.response?.data || err.message);
          return null;
        });

    setLoading(true);
    safeGet(`/applications/${applicationId}`).then(async (app) => {
      if (!app) {
        showError('Application not found');
        setLoading(false);
        return;
      }
      setApplication(app);

      // Fetch related rows in parallel; each is independent.
      const [tankRows, ngoRow, deptRow, districtRow, chainRows, docRows] = await Promise.all([
        safeGet(`/tanks?tank_id=${app.tank_id}`),
        safeGet(`/users/${app.applied_by}`),
        safeGet(`/departments/${app.department_id}`),
        safeGet(`/districts/${app.district_id}`),
        safeGet(`/applications/${applicationId}/chain`),
        safeGet(`/applications/${applicationId}/docs`),
      ]);

      setTank(Array.isArray(tankRows) ? tankRows[0] || null : tankRows);
      setNgo(ngoRow);
      setDepartment(deptRow);
      setDistrict(districtRow);
      let chainArr = Array.isArray(chainRows) ? chainRows : [];

      // Self-heal: if no chain exists (application predates the workflow code,
      // or was created before officer mappings were seeded), try to seed it
      // silently and re-fetch. The endpoint is idempotent.
      if (chainArr.length === 0) {
        try {
          await API.post(`/applications/${applicationId}/seed-chain`);
          const reseed = await safeGet(`/applications/${applicationId}/chain`);
          chainArr = Array.isArray(reseed) ? reseed : [];
        } catch (err) {
          console.warn('auto seed-chain failed:', err?.response?.data || err.message);
        }
      }

      setChain(chainArr);
      setDocs(Array.isArray(docRows) ? docRows : []);

      // Resolve approver names lazily.
      const approverIds = Array.from(new Set(chainArr.map((r) => r.user_id).filter(Boolean)));
      const userMap = {};
      await Promise.all(
        approverIds.map((uid) =>
          safeGet(`/users/${uid}`).then((u) => {
            if (u) userMap[uid] = u;
          })
        )
      );
      setChainUsers(userMap);
      setLoading(false);
    });
  }, [applicationId, navigate, authReady, isAuthenticated]);

  const docMeta = (() => {
    try { return JSON.parse(application?.doc_path || '{}'); } catch { return {}; }
  })();

  const activitiesByCategory = (() => {
    const out = {};
    String(application?.activity_ids || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .forEach((tok) => {
        const [catId, sub] = tok.split(':');
        const key = ACTIVITY_CATEGORIES[catId] || `Category ${catId}`;
        out[key] = out[key] || [];
        if (sub) out[key].push(sub);
      });
    return out;
  })();

  const activeStep = chain.find((s) => String(s.status).toUpperCase() === 'PENDING') || null;
  // Action buttons appear only when the *currently pending* step is assigned
  // to this user AND the application is still in flight (not APPROVED/REJECTED).
  const appStatusUpper = String(application?.status || '').toUpperCase();
  const isTerminal = appStatusUpper === 'APPROVED' || appStatusUpper === 'REJECTED';
  const isMyTurn =
    !isTerminal && !!(activeStep && userId && Number(activeStep.user_id) === Number(userId));

  const openAction = (mode) => {
    setActionMode(mode);
    setComments('');
    setReason('');
    setActionError('');
  };

  const cancelAction = () => {
    setActionMode(null);
    setComments('');
    setReason('');
    setActionError('');
  };

  const submitAction = async () => {
    // FRS: Reject and Return require a mandatory reason from the dropdown plus remarks.
    if ((actionMode === 'reject' || actionMode === 'return') && !reason) {
      setActionError('Reason is required.');
      return;
    }
    if ((actionMode === 'reject' || actionMode === 'return') && !comments.trim()) {
      setActionError('Remarks are required.');
      return;
    }
    setSubmitting(true);
    setActionError('');
    try {
      const endpoint = actionMode === 'return' ? 'return' : actionMode;
      const url = `/applications/${applicationId}/${endpoint}`;
      const body = { comments: comments.trim() };
      if (reason) body.reason = reason;
      const res = await API.post(url, body);
      if (res?.data?.success) {
        const msg =
          actionMode === 'approve' ? 'Approved'
          : actionMode === 'return' ? 'Returned to applicant'
          : 'Rejected';
        showSuccess(res.data.message || msg);
        redirectPage('/dashboard');
      } else {
        setActionError(res?.data?.message || `${actionMode} failed`);
      }
    } catch (err) {
      setActionError(err?.response?.data?.message || `${actionMode} failed`);
    } finally {
      setSubmitting(false);
    }
  };

  const stepStatusColor = (status) => {
    const s = String(status).toUpperCase();
    if (s === 'APPROVED') return 'bg-green-500';
    if (s === 'REJECTED') return 'bg-red-500';
    if (s === 'PENDING') return 'bg-orange-400';
    return 'bg-gray-300';
  };

  return (
    <div className="bg-gradient-to-br from-gray-50 via-white to-gray-100 min-h-screen font-['Inter',sans-serif] pt-16 sm:pt-20">
      {/* Breadcrumb */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-3 sm:px-6">
          <div className="flex items-center py-3">
            <button
              onClick={() => (window.location.href = '/dashboard')}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 mr-4 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div className="flex items-center space-x-2 text-sm">
              <span className="text-gray-500">Tamil Nadu NOC Portal</span>
              <span className="text-gray-300">→</span>
              <span className="text-gray-500">Dashboard</span>
              <span className="text-gray-300">→</span>
              <span className="text-indigo-600 font-medium">NOC Workflow</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-8">
        <div className="mb-6 sm:mb-8">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">NOC Application Review</h1>
          <p className="text-gray-600">Review the application, see the approval chain, and act if it's your turn.</p>
        </div>

        {loading && (
          <div className="bg-white rounded-xl shadow p-8 text-center text-gray-500">Loading…</div>
        )}

        {!loading && application && (
          <>
            {/* Header card */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden mb-6 sm:mb-8">
              <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-4 sm:px-6 py-4">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-2 sm:space-y-0">
                  <div>
                    <h2 className="text-lg sm:text-xl font-semibold text-white">
                      NOC ID: NOC{String(application.application_id).padStart(3, '0')}
                    </h2>
                    <p className="text-indigo-100 text-sm">
                      Status: <span className="font-semibold uppercase">{application.status}</span>
                      {(() => {
                        const sla = slaForStep(application, activeStep);
                        if (!sla) return null;
                        const cls = sla.overdue
                          ? 'bg-red-500 text-white'
                          : sla.daysLeft <= 2
                          ? 'bg-orange-400 text-white'
                          : 'bg-white text-indigo-700';
                        return (
                          <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold ${cls}`}>
                            SLA: {sla.overdue ? `${Math.abs(sla.daysLeft)}d overdue` : `${sla.daysLeft}d left`}
                          </span>
                        );
                      })()}
                    </p>
                  </div>
                  <div className="text-left sm:text-right">
                    <span className="text-indigo-100 text-sm">Submitted on</span>
                    <div className="text-white font-medium">{formatDate(application.created_at)}</div>
                  </div>
                </div>
              </div>

              <div className="p-4 sm:p-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Tank / Application meta */}
                  <div className="space-y-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <Building2 className="w-5 h-5 text-indigo-600" />
                      <h3 className="text-lg font-semibold text-gray-900">Tank Details</h3>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                      <div>
                        <label className="text-sm font-medium text-gray-600">Unique Tank ID</label>
                        <p className="text-base font-mono text-gray-900">
                          {application.tank_id ? `TN-${String(application.tank_id).padStart(6, '0')}` : '-'}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Tank Name</label>
                        <p className="text-base font-semibold text-gray-900">
                          {tank?.water_body_name || `Tank #${application.tank_id}`}
                        </p>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-sm font-medium text-gray-600">District</label>
                          <p className="text-base text-gray-900">{district?.district_name || tank?.district_name || '-'}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600">Block</label>
                          <p className="text-base text-gray-900">{tank?.block_name || '-'}</p>
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Village</label>
                        <div className="flex items-center space-x-2">
                          <MapPin className="w-4 h-4 text-gray-500" />
                          <p className="text-base text-gray-900">{tank?.village_or_town_name || '-'}</p>
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Department</label>
                        <p className="text-base text-gray-900">
                          {department?.department_name || tank?.owned_by || '-'}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Submitted By</label>
                        <div className="flex items-center justify-between">
                          <p className="text-base text-gray-900">
                            {ngo?.full_name || `User #${application.applied_by}`}
                          </p>
                          {ngo && (
                            <button
                              onClick={() => setShowNGOProfile(true)}
                              className="inline-flex items-center px-3 py-1 text-xs font-medium text-indigo-700 bg-indigo-100 border border-indigo-200 rounded-lg hover:bg-indigo-200"
                            >
                              <Eye className="w-3 h-3 mr-1" />
                              View Profile
                            </button>
                          )}
                        </div>
                      </div>
                      {application.remarks && (
                        <div>
                          <label className="text-sm font-medium text-gray-600">Remarks</label>
                          <p className="text-base text-gray-900">{application.remarks}</p>
                        </div>
                      )}
                      {docMeta.lumpsumAmount && (
                        <div>
                          <label className="text-sm font-medium text-gray-600">Lumpsum Amount</label>
                          <p className="text-lg font-bold text-indigo-600">₹{docMeta.lumpsumAmount}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Activities + documents */}
                  <div className="space-y-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <FileText className="w-5 h-5 text-indigo-600" />
                      <h3 className="text-lg font-semibold text-gray-900">Activities & Documents</h3>
                    </div>

                    {Object.keys(activitiesByCategory).length === 0 && (
                      <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-500">
                        No activities recorded.
                      </div>
                    )}
                    {Object.entries(activitiesByCategory).map(([cat, subs]) => (
                      <div key={cat} className="bg-gray-50 rounded-lg p-4">
                        <label className="text-sm font-medium text-gray-600 block mb-2">{cat}</label>
                        {subs.length === 0 ? (
                          <p className="text-xs text-gray-500">Selected (no sub-items)</p>
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            {subs.map((s, i) => (
                              <span
                                key={`${s}-${i}`}
                                className="inline-flex px-3 py-1 text-sm font-medium bg-indigo-100 text-indigo-700 rounded-lg border border-indigo-200"
                              >
                                {s}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}

                    <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                      <label className="text-sm font-medium text-gray-600 block mb-1">Submitted Documents</label>
                      {docs.length === 0 ? (
                        <p className="text-xs text-gray-500">No documents uploaded.</p>
                      ) : (
                        <div className="space-y-3">
                          {docs.map((d) => {
                            const url = `${FILE_BASE_URL}/${String(d.stored_path).replace(/^uploads\//, '')}`;
                            const labelMap = {
                              before_photo: 'Before Photo',
                              project_proposal: 'Project Proposal',
                              kml_file: 'KML File',
                            };
                            const isImage = /^image\//i.test(d.mime_type || '') ||
                              /\.(png|jpe?g|gif|webp)$/i.test(d.original_name || '');
                            return (
                              <div
                                key={d.doc_id}
                                className="p-2 bg-white rounded border border-gray-200"
                              >
                                <div className="flex items-center justify-between mb-2">
                                  <div className="min-w-0">
                                    <p className="text-xs font-medium text-gray-900">
                                      {labelMap[d.doc_type] || d.doc_type}
                                    </p>
                                    <p className="text-xs text-gray-500 truncate">{d.original_name}</p>
                                  </div>
                                  <button
                                    onClick={() => window.open(url, '_blank')}
                                    className="ml-3 inline-flex items-center px-2 py-1 text-xs font-medium text-indigo-700 bg-indigo-100 border border-indigo-200 rounded hover:bg-indigo-200 flex-shrink-0"
                                  >
                                    <Eye className="w-3 h-3 mr-1" /> View
                                  </button>
                                </div>
                                {isImage && (
                                  <a href={url} target="_blank" rel="noreferrer" className="block">
                                    <img
                                      src={url}
                                      alt={d.original_name}
                                      className="w-full max-h-48 object-contain rounded border border-gray-100 bg-gray-50"
                                      loading="lazy"
                                    />
                                  </a>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Action buttons (only for the active approver) */}
            {isMyTurn && (
              <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4 sm:p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Take Action</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <button
                    onClick={() => openAction('approve')}
                    disabled={submitting}
                    className={`flex items-center justify-center space-x-3 p-4 text-white rounded-lg shadow-lg transition-all ${
                      actionMode === 'approve'
                        ? 'bg-green-700 ring-2 ring-green-300'
                        : 'bg-green-600 hover:bg-green-700'
                    } disabled:opacity-50`}
                  >
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-medium">Approve</span>
                  </button>
                  <button
                    onClick={() => openAction('return')}
                    disabled={submitting}
                    className={`flex items-center justify-center space-x-3 p-4 text-white rounded-lg shadow-lg transition-all ${
                      actionMode === 'return'
                        ? 'bg-amber-600 ring-2 ring-amber-300'
                        : 'bg-amber-500 hover:bg-amber-600'
                    } disabled:opacity-50`}
                  >
                    <ArrowLeft className="w-5 h-5" />
                    <span className="font-medium">Return to Applicant</span>
                  </button>
                  <button
                    onClick={() => openAction('reject')}
                    disabled={submitting}
                    className={`flex items-center justify-center space-x-3 p-4 text-white rounded-lg shadow-lg transition-all ${
                      actionMode === 'reject'
                        ? 'bg-red-700 ring-2 ring-red-300'
                        : 'bg-red-600 hover:bg-red-700'
                    } disabled:opacity-50`}
                  >
                    <XCircle className="w-5 h-5" />
                    <span className="font-medium">Reject</span>
                  </button>
                </div>
                {/* L1/L2 wording removed; users see only the action verbs. */}

                {actionMode && (
                  <div className="mt-5 border-t border-gray-200 pt-5 space-y-3">
                    {(actionMode === 'reject' || actionMode === 'return') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {actionMode === 'reject' ? 'Rejection reason' : 'Return reason'}{' '}
                          <span className="text-red-600">*</span>
                        </label>
                        <select
                          value={reason}
                          onChange={(e) => {
                            setReason(e.target.value);
                            if (actionError) setActionError('');
                          }}
                          className="w-full rounded-lg border border-gray-300 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                          <option value="">— Select a reason —</option>
                          {(actionMode === 'reject' ? REJECT_REASONS : RETURN_REASONS).map((r) => (
                            <option key={r} value={r}>{r}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {actionMode === 'approve'
                          ? 'Approval remarks (optional)'
                          : actionMode === 'return'
                          ? 'Remarks to applicant '
                          : 'Rejection remarks '}
                        {actionMode !== 'approve' && <span className="text-red-600">*</span>}
                      </label>
                      <textarea
                        value={comments}
                        onChange={(e) => {
                          setComments(e.target.value);
                          if (actionError) setActionError('');
                        }}
                        rows={4}
                        placeholder={
                          actionMode === 'approve'
                            ? 'Add any notes for the next level…'
                            : actionMode === 'return'
                            ? 'Tell the applicant what to revise or clarify…'
                            : 'Explain why this application is being rejected…'
                        }
                        className="w-full rounded-lg border border-gray-300 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                    {actionMode === 'return' && (
                      <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                        Note: an application can be returned only once. On resubmission by the applicant the SLA timeline resumes.
                      </p>
                    )}
                    {actionError && (
                      <p className="text-sm text-red-600">{actionError}</p>
                    )}
                    <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
                      <button
                        onClick={cancelAction}
                        disabled={submitting}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={submitAction}
                        disabled={submitting}
                        className={`px-4 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50 ${
                          actionMode === 'approve'
                            ? 'bg-green-600 hover:bg-green-700'
                            : actionMode === 'return'
                            ? 'bg-amber-500 hover:bg-amber-600'
                            : 'bg-red-600 hover:bg-red-700'
                        }`}
                      >
                        {submitting
                          ? 'Submitting…'
                          : actionMode === 'approve'
                          ? 'Confirm Approval'
                          : actionMode === 'return'
                          ? 'Confirm Return'
                          : 'Confirm Rejection'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {!isMyTurn && activeStep && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
                {['L1 A', 'L2 A', 'Master'].includes(userInfo?.role_type)
                  ? <>You are viewing this application in <b>read-only</b> mode as <b>{userInfo.role_type}</b>. Only the assigned Level-1 / Level-2 approvers can take action.</>
                  : <>This application is currently under review by the assigned department officer. Action buttons appear only for the officer whose turn it is.</>}
              </div>
            )}

            {!activeStep && chain.length === 0 && (
              <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 sm:p-5 text-sm text-amber-900">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-200 text-amber-800 flex items-center justify-center font-bold text-sm">!</div>
                  <div className="flex-1">
                    <p className="font-semibold mb-1">Approval chain could not be built — officer mapping pending from department</p>
                    <p className="leading-relaxed">
                      This tank belongs to the{' '}
                      <b>{department?.department_name || tank?.owned_by || '—'}</b> department in the{' '}
                      <b>{district?.district_name || tank?.district_name || '—'}</b> district
                      {tank?.section_name && tank.section_name !== '-' ? (
                        <> (section: <b>{tank.section_name}</b>)</>
                      ) : null}.
                      No Level-1 / Level-2 officer has been mapped for this combination yet, so
                      the application cannot be routed.
                    </p>
                    <p className="leading-relaxed mt-2">
                      <b>Action required from the department:</b> please provide the officer list
                      (name, official email, designation) for this department + district
                      combination so the portal can route applications.
                    </p>
                    <p className="text-xs text-amber-700 mt-2">
                      Reference: Application <b>NOC{String(application.application_id).padStart(3, '0')}</b>.
                      Until officers are onboarded, this application will remain unrouted.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {!activeStep && chain.length > 0 && (() => {
              // Am I the L1 officer for this application?
              const l1Step = chain
                .slice()
                .sort((a, b) => Number(a.level) - Number(b.level))[0];
              const isL1Officer = l1Step && Number(l1Step.user_id) === Number(userId);
              const isApproved = appStatusUpper === 'APPROVED' || appStatusUpper === 'DEEMED_APPROVED';
              // Has the completion certificate already been issued?
              const completionIssued = !!application?.completion_issued_at;
              const issuedOn = completionIssued
                ? new Date(application.completion_issued_at).toLocaleDateString('en-IN')
                : null;
              const handleGenerateCompletion = () => {
                if (!window.confirm('Generate the Work Completion Certificate? Use this only after the on-ground work has been verified as complete. This action is recorded and the certificate becomes viewable to everyone.')) return;
                const w = window.open(`${API_BASE_URL}/applications/${applicationId}/completion-certificate`, '_blank');
                // Refresh after a short delay so the issued-on timestamp shows up
                setTimeout(() => window.location.reload(), 800);
                return w;
              };
              return (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm text-emerald-800 flex items-center justify-between flex-wrap gap-3">
                  <span>
                    Workflow complete — every level has acted on this application.
                    {completionIssued && (
                      <span className="block text-xs text-teal-700 mt-1">
                        Work Completion Certificate issued on <b>{issuedOn}</b>.
                      </span>
                    )}
                  </span>
                  <div className="flex flex-wrap gap-2">
                    {isApproved && (
                      <button
                        onClick={() => window.open(`${API_BASE_URL}/applications/${applicationId}/certificate`, '_blank')}
                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded"
                      >
                        View NOC Certificate
                      </button>
                    )}

                    {/* Completion certificate behaviour:
                        - Not yet issued + I am L1 → show "Generate Completion Certificate"
                        - Already issued → show "View Completion Certificate" to everyone */}
                    {isApproved && completionIssued && (
                      <button
                        onClick={() => window.open(`${API_BASE_URL}/applications/${applicationId}/completion-certificate`, '_blank')}
                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded"
                      >
                        View Completion Certificate
                      </button>
                    )}
                    {isApproved && !completionIssued && isL1Officer && (
                      <button
                        onClick={handleGenerateCompletion}
                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded"
                        title="Issue the Work Completion Certificate (only the Level 1 officer can do this)"
                      >
                        Generate Completion Certificate
                      </button>
                    )}
                  </div>
                </div>
              );
            })()}
          </>
        )}

        {/* NGO Profile Modal */}
        {showNGOProfile && ngo && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-indigo-600 to-purple-600">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Building2 className="w-6 h-6 text-white" />
                    <h3 className="text-lg font-semibold text-white">NGO Profile</h3>
                  </div>
                  <button
                    onClick={() => setShowNGOProfile(false)}
                    className="text-white hover:text-gray-200 transition-colors"
                  >
                    <XCircle className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="p-6 overflow-y-auto max-h-[calc(90vh-160px)] space-y-4">
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div>
                    <label className="text-sm font-medium text-gray-600">Organization / Name</label>
                    <p className="text-base font-semibold text-gray-900">{ngo.full_name || '-'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Email</label>
                    <p className="text-base text-indigo-600">{ngo.email_id || '-'}</p>
                  </div>
                  {ngo.mobile_number && (
                    <div>
                      <label className="text-sm font-medium text-gray-600">Mobile</label>
                      <p className="text-base text-gray-900">{ngo.mobile_number}</p>
                    </div>
                  )}
                  {ngo.pan_number && (
                    <div>
                      <label className="text-sm font-medium text-gray-600">PAN</label>
                      <p className="text-base text-gray-900">{ngo.pan_number}</p>
                    </div>
                  )}
                  {ngo.gst_number && (
                    <div>
                      <label className="text-sm font-medium text-gray-600">GST</label>
                      <p className="text-base text-gray-900">{ngo.gst_number}</p>
                    </div>
                  )}
                </div>

                {(() => {
                  const docs = [
                    ['Registration Certificate', ngo.registration_cretificate_path],
                    ['PAN Card', ngo.pan_card_path],
                    ['Organization Profile', ngo.organization_profile_path],
                    ['Similar Project Details', ngo.similar_project_details_path],
                    ['Resolution Copy', ngo.resolution_copy_path],
                  ].filter(([, p]) => typeof p === 'string' && p.trim().length > 0);

                  if (docs.length === 0) return null;

                  const isDataUri = (raw) => /^data:/i.test(raw);
                  // Show a clean filename only (strip the upload directory + the
                  // multer hash-and-timestamp suffix we add at upload time).
                  const prettyName = (raw) => {
                    if (isDataUri(raw)) return 'Inline image (legacy upload)';
                    const base = raw.split('/').pop() || raw;
                    return base.replace(/_\d{10,}-\d+(?=\.[^.]+$)/, '');
                  };
                  const fileUrl = (raw) =>
                    isDataUri(raw) ? raw : `${FILE_BASE_URL}/${raw.replace(/^uploads\//, '')}`;

                  const isImagePath = (raw) =>
                    isDataUri(raw) || /\.(png|jpe?g|gif|webp)$/i.test(raw || '');
                  return (
                    <div className="bg-blue-50 rounded-lg p-4">
                      <h4 className="text-base font-semibold text-gray-900 mb-3">Registration Documents</h4>
                      <div className="space-y-3">
                        {docs.map(([label, raw]) => (
                          <div
                            key={label}
                            className="p-3 bg-white rounded-lg border border-gray-200"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-gray-900">{label}</p>
                                <p className="text-xs text-gray-500 truncate">{prettyName(raw)}</p>
                              </div>
                              <button
                                onClick={() => window.open(fileUrl(raw), '_blank')}
                                className="inline-flex items-center px-3 py-1 text-xs font-medium text-indigo-700 bg-indigo-100 border border-indigo-200 rounded-lg hover:bg-indigo-200 ml-3 flex-shrink-0"
                              >
                                <Eye className="w-3 h-3 mr-1" />
                                View
                              </button>
                            </div>
                            {isImagePath(raw) && (
                              <a href={fileUrl(raw)} target="_blank" rel="noreferrer" className="block">
                                <img
                                  src={fileUrl(raw)}
                                  alt={label}
                                  className="w-full max-h-48 object-contain rounded border border-gray-100 bg-gray-50"
                                  loading="lazy"
                                />
                              </a>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>

              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end">
                <button
                  onClick={() => setShowNGOProfile(false)}
                  className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
